import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { cognitoAdminService } from '../../services/cognitoAdminService';
import { databaseService } from '../../services/databaseService';
import { REGION_BOUNDARIES } from '../../data/regionBoundaries';
import UserManagement from './UserManagement';
import DatabaseViewer from './DatabaseViewer';
import DatabaseSummary from './DatabaseSummary';
import Reports from './Reports';
import WaterConsumptionReport from './WaterConsumptionReport';
import DatabaseExplorer from './DatabaseExplorer';
import NDVIAnalysis from './NDVIAnalysis';
import ParkManager from './ParkManager';
import ASKILiveData from './ASKILiveData';
import ParkVerification from './ParkVerification';
import AskiAnalysis from './AskiAnalysis';
import ASKIMonthlyReport from './ASKIMonthlyReport';
import DataInventoryReport from './DataInventoryReport';
import GISDataAnalysis from './GISDataAnalysis';
import AdminRapor from './AdminRapor';
import ProphetForecast from './ProphetForecast';
import IoTDashboard from './IoTDashboard';
import AnomalyDetection from './AnomalyDetection';
import styles from './Dashboard.module.css';

const AdminDashboard = ({ onNavigateToMap }) => {
  const { user, userRole, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [totalUsers, setTotalUsers] = useState(null);
  const [usersLoading, setUsersLoading] = useState(true);
  
  // Sistem İzleme için state'ler
  const [dbStats, setDbStats] = useState({
    connected: false,
    totalSchemas: 0,
    totalTables: 0,
    loading: true,
  });
  const [systemLogs, setSystemLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize] = useState(10); // Her sayfada 10 log
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Genel Bakış' },
    { id: 'data', label: 'Veriler' },
    { id: 'dbmanage', label: 'DB Yönetim' },
    { id: 'parkmanager', label: 'Park Yönetim' },
    { id: 'ndvi', label: 'Uydu Analiz' },
    { id: 'reports', label: 'Park Raporları' },
    { id: 'water', label: 'Su Tüketimi' },
    { id: 'users', label: 'Kullanıcı Yönetimi' },
    { id: 'system', label: 'Sistem Ayarları' },
    { id: 'monitoring', label: 'Sistem İzleme' },
    { id: 'aski-live', label: 'Canlı Veri (ASKİ)' },
    { id: 'park-verify', label: 'Park Doğrulama' },
    { id: 'aski-analysis', label: 'Dönem Analizi' },
    { id: 'aski-report', label: 'ASKI Rapor' },
    { id: 'data-inventory', label: 'Veri Envanteri' },
    { id: 'gis-analysis', label: 'GIS Analiz' },
    { id: 'rapor', label: 'Rapor' },
    { id: 'ai-forecast', label: 'AI Tahmin' },
    { id: 'anomaly', label: 'Anomali Tespiti' },
    { id: 'iot', label: 'IoT Cihazlar' },
  ];

  // Cognito'dan toplam kullanıcı sayısını çek
  useEffect(() => {
    const loadUserCount = async () => {
      try {
        setUsersLoading(true);
        const usersList = await cognitoAdminService.listUsers();
        const previousCount = totalUsers;
        setTotalUsers(usersList.length);
        console.log('✅ Toplam kullanıcı sayısı:', usersList.length);
        
        // İlk yüklemede log kaydet
        if (previousCount === null) {
          try {
            const { logEvent } = await import('../../services/logService');
            await logEvent('INFO', 'system', `Toplam ${usersList.length} kullanıcı kayıtlı`, {
              userEmail: user?.email,
              userRole: userRole,
            });
          } catch (logError) {
            console.warn('Log kaydedilemedi:', logError);
          }
        }
      } catch (err) {
        console.error('❌ Kullanıcı sayısı yüklenemedi:', err);
        // Hata durumunda null bırak, "--" gösterilecek
        setTotalUsers(null);
        
        // Identity Pool hatası ise kullanıcıya bilgi ver
        if (err.message?.includes('Identity Pool') || err.message?.includes('credentials')) {
          console.warn('⚠️ Identity Pool kurulumu gerekiyor. COGNITO_IDENTITY_POOL_SETUP.md dosyasına bakın.');
        }
      } finally {
        setUsersLoading(false);
      }
    };

    loadUserCount();
  }, []);

  // Kullanıcı yönetimi sekmesinde kullanıcı eklendi/silindiğinde sayıyı güncelle
  useEffect(() => {
    if (activeTab === 'users') {
      // Kullanıcı yönetimi sekmesine geçildiğinde sayıyı yenile
      const loadUserCount = async () => {
        try {
          const usersList = await cognitoAdminService.listUsers();
          setTotalUsers(usersList.length);
        } catch (err) {
          console.error('❌ Kullanıcı sayısı güncellenemedi:', err);
        }
      };
      loadUserCount();
    }
  }, [activeTab]);

  // Veritabanı istatistiklerini yükle
  useEffect(() => {
    const loadDatabaseStats = async () => {
      try {
        setDbStats(prev => ({ ...prev, loading: true }));
        const stats = await databaseService.getDatabaseStats();
        const wasConnected = dbStats.connected;
        setDbStats({
          ...stats,
          loading: false,
        });
        
        // Veritabanı bağlantı durumu değiştiyse log kaydet
        if (wasConnected !== stats.connected) {
          try {
            const { logEvent } = await import('../../services/logService');
            await logEvent(
              stats.connected ? 'SUCCESS' : 'ERROR',
              'database',
              `Veritabanı bağlantısı: ${stats.connected ? 'Başarılı' : 'Başarısız'}`,
              {
                userEmail: user?.email,
                userRole: userRole,
                totalSchemas: stats.totalSchemas,
                totalTables: stats.totalTables,
              }
            );
          } catch (logError) {
            // Log hatası kritik değil
            console.warn('Log kaydedilemedi:', logError);
          }
        }
      } catch (err) {
        console.error('❌ Veritabanı istatistikleri yüklenemedi:', err);
        setDbStats({
          connected: false,
          totalSchemas: 0,
          totalTables: 0,
          loading: false,
          error: err.message,
        });
        
        // Hata logunu kaydet
        try {
          const { logEvent } = await import('../../services/logService');
          await logEvent('ERROR', 'database', `Veritabanı bağlantı hatası: ${err.message}`, {
            userEmail: user?.email,
            userRole: userRole,
          });
        } catch (logError) {
          console.warn('Log kaydedilemedi:', logError);
        }
      }
    };

    // Sistem Ayarları veya Sistem İzleme sekmesine geçildiğinde yükle
    if (activeTab === 'system' || activeTab === 'monitoring') {
      loadDatabaseStats();
      
      // Her 30 saniyede bir yenile
      const interval = setInterval(loadDatabaseStats, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab, dbStats.connected, user, userRole]);

  // Sistem loglarını CloudWatch'tan yükle (pagination ile)
  useEffect(() => {
    if (activeTab === 'monitoring') {
      const loadLogs = async () => {
        try {
          setLogsLoading(true);
          const { getLogs } = await import('../../services/logService');
          const offset = (logsPage - 1) * logsPageSize;
          // Daha fazla log çekiyoruz (toplam sayıyı tahmin etmek için)
          // CloudWatch'tan tüm logları çekip frontend'de pagination yapıyoruz
          const allLogs = await getLogs(1000); // Son 1000 logu çek
          setLogsTotal(allLogs.length);
          
          // Frontend'de pagination yap
          const startIndex = offset;
          const endIndex = startIndex + logsPageSize;
          const paginatedLogs = allLogs.slice(startIndex, endIndex);
          
          setSystemLogs(paginatedLogs);
          setLogsLoading(false);
        } catch (err) {
          console.error('❌ Loglar yüklenemedi:', err);
          setSystemLogs([]);
          setLogsLoading(false);
        }
      };

      loadLogs();
      // Sayfa değiştiğinde de yeniden yükle
    }
  }, [activeTab, logsPage, logsPageSize]);

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.dashboardHeader}>
        <div>
          <h1>Admin Dashboard</h1>
          <p>{user?.email || user?.username} &middot; <span className={styles.roleBadge}>{userRole}</span></p>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button 
            onClick={logout}
            className={styles.logoutButton}
          >
            Çıkış Yap
          </button>
        </div>
      </div>

      <div className={styles.dashboardTabs}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.dashboardContent}>
        {activeTab === 'overview' && (
          <div className={styles.overviewContainer}>
            <div className={styles.overviewHeader}>
              <h2>Genel Bakış</h2>
              <button 
                onClick={onNavigateToMap}
                className={styles.mapButton}
              >
                Harita Uygulamasını Aç
              </button>
            </div>
            <div className={styles.monitoringGrid}>
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <span className={styles.metricDot} style={{ background: dbStats.connected ? '#22c55e' : '#ef4444' }}></span>
                  <h3>Sistem Durumu</h3>
                </div>
                <div className={styles.metricValue}>
                  {dbStats.loading ? '...' : dbStats.connected ? 'Sağlıklı' : 'Hata'}
                </div>
                <div className={styles.metricLabel}>
                  {dbStats.loading 
                    ? 'Kontrol ediliyor...' 
                    : dbStats.connected 
                      ? 'Tüm servisler çalışıyor' 
                      : dbStats.error || 'Bağlantı hatası'}
                </div>
                <div className={styles.metricBar}>
                  <div 
                    className={styles.metricBarFill} 
                    style={{ 
                      width: dbStats.connected ? '100%' : '0%', 
                      background: dbStats.connected ? '#22c55e' : '#ef4444' 
                    }}
                  ></div>
                </div>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <span className={styles.metricDot} style={{ background: '#3b82f6' }}></span>
                  <h3>Veritabanı</h3>
                </div>
                <div className={styles.metricValue}>
                  {dbStats.loading ? '...' : dbStats.totalTables}
                </div>
                <div className={styles.metricLabel}>
                  {dbStats.loading ? 'Yükleniyor...' : `${dbStats.totalSchemas} şemada toplam tablo`}
                </div>
                <div className={styles.metricBar}>
                  <div 
                    className={styles.metricBarFill} 
                    style={{ 
                      width: dbStats.loading ? '0%' : `${Math.min((dbStats.totalTables / 100) * 100, 100)}%`, 
                      background: '#3b82f6' 
                    }}
                  ></div>
                </div>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <span className={styles.metricDot} style={{ background: '#f59e0b' }}></span>
                  <h3>Toplam Kullanıcı</h3>
                </div>
                <div className={styles.metricValue}>
                  {usersLoading ? '...' : totalUsers !== null ? totalUsers : '--'}
                </div>
                <div className={styles.metricLabel}>
                  {usersLoading ? 'Yükleniyor...' : 'Aktif kullanıcı'}
                </div>
                <div className={styles.metricBar}>
                  <div 
                    className={styles.metricBarFill} 
                    style={{ 
                      width: usersLoading || totalUsers === null ? '0%' : `${Math.min((totalUsers / 100) * 100, 100)}%`, 
                      background: '#f59e0b' 
                    }}
                  ></div>
                </div>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <span className={styles.metricDot} style={{ background: '#10b981' }}></span>
                  <h3>Toplam Bölge Sayısı</h3>
                </div>
                <div className={styles.metricValue}>
                  {Object.keys(REGION_BOUNDARIES).length}
                </div>
                <div className={styles.metricLabel}>
                  Kayıtlı bölge
                </div>
                <div className={styles.metricBar}>
                  <div 
                    className={styles.metricBarFill} 
                    style={{ 
                      width: `${Math.min((Object.keys(REGION_BOUNDARIES).length / 20) * 100, 100)}%`, 
                      background: '#10b981' 
                    }}
                  ></div>
                </div>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <span className={styles.metricDot} style={{ background: '#8b5cf6' }}></span>
                  <h3>Son Güncelleme</h3>
                </div>
                <div className={styles.metricValue}>
                  {new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div className={styles.metricLabel}>Gerçek zamanlı veri</div>
                <div className={styles.metricTrend}>Canlı</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'data' && (
          <div className={styles.dataContainer}>
            <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', padding: '20px 0' }}>
              <button
                onClick={() => setActiveTab('data-summary')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Veritabanı Özeti
              </button>
              <button
                onClick={() => setActiveTab('data-viewer')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#64748b',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Tablo Görüntüleyici
              </button>
            </div>
            <DatabaseViewer />
          </div>
        )}
        {activeTab === 'data-summary' && (
          <div className={styles.dataContainer}>
            <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', padding: '20px 0' }}>
              <button
                onClick={() => setActiveTab('data-summary')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Veritabanı Özeti
              </button>
              <button
                onClick={() => setActiveTab('data-viewer')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#64748b',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Tablo Görüntüleyici
              </button>
            </div>
            <DatabaseSummary />
          </div>
        )}
        {activeTab === 'data-viewer' && (
          <div className={styles.dataContainer}>
            <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', padding: '20px 0' }}>
              <button
                onClick={() => setActiveTab('data-summary')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#64748b',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Veritabanı Özeti
              </button>
              <button
                onClick={() => setActiveTab('data-viewer')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Tablo Görüntüleyici
              </button>
            </div>
            <DatabaseViewer />
          </div>
        )}

        {activeTab === 'dbmanage' && (
          <div style={{ padding: 0 }}>
            <div style={{ padding: '8px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => window.open('/db', '_blank')} style={{ padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: '#0f172a', color: '#38bdf8', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 14 }}>{'→'}</span> DB Modu (Tam Ekran)
              </button>
            </div>
            <DatabaseExplorer />
          </div>
        )}

        {activeTab === 'parkmanager' && (
          <div style={{ padding: 0 }}>
            <ParkManager />
          </div>
        )}

        {activeTab === 'ndvi' && (
          <div style={{ padding: 0 }}>
            <NDVIAnalysis />
          </div>
        )}

        {activeTab === 'reports' && (
          <div className={styles.reportsContainer}>
            <Reports />
          </div>
        )}

        {activeTab === 'water' && (
          <div className={styles.reportsContainer}>
            <WaterConsumptionReport />
          </div>
        )}

        {activeTab === 'users' && (
          <div className={styles.usersContainer}>
            <UserManagement 
              onUserListChange={(count) => {
                // Kullanıcı listesi değiştiğinde sayıyı güncelle
                setTotalUsers(count);
              }}
            />
          </div>
        )}

        {activeTab === 'system' && (
          <div className={styles.systemContainer}>
            <div className={styles.settingsHeader}>
              <h2>Sistem Ayarları</h2>
              <p className={styles.settingsSubtitle}>Uygulama ve sistem yapılandırmaları</p>
            </div>
            
            <div className={styles.settingsGrid}>
              <div className={styles.settingCard}>
                <h3>Güvenlik Ayarları</h3>
                <p>Şifre politikaları, MFA ve erişim kontrolleri</p>
                <button className={styles.settingButton}>Yapılandır</button>
              </div>
              
              <div className={styles.settingCard}>
                <h3>E-posta Ayarları</h3>
                <p>Bildirim ve rapor e-posta yapılandırmaları</p>
                <button className={styles.settingButton}>Yapılandır</button>
              </div>
              
              <div className={styles.settingCard}>
                <h3>Veritabanı Ayarları</h3>
                <p>
                  {dbStats.loading ? (
                    'Yükleniyor...'
                  ) : dbStats.connected ? (
                    `Bağlı - ${dbStats.totalSchemas} şema, ${dbStats.totalTables} tablo`
                  ) : (
                    `Bağlantı hatası: ${dbStats.error || 'Bilinmeyen hata'}`
                  )}
                </p>
                <button className={styles.settingButton}>Yapılandır</button>
              </div>
              
              <div className={styles.settingCard}>
                <h3>API Ayarları</h3>
                <p>Harici API entegrasyonları ve anahtarlar</p>
                <button className={styles.settingButton}>Yapılandır</button>
              </div>
              
              <div className={styles.settingCard}>
                <h3>Raporlama Ayarları</h3>
                <p>Rapor formatları ve otomatik gönderim</p>
                <button className={styles.settingButton}>Yapılandır</button>
              </div>
              
              <div className={styles.settingCard}>
                <h3>Genel Ayarlar</h3>
                <p>Uygulama genel yapılandırmaları</p>
                <button className={styles.settingButton}>Yapılandır</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'monitoring' && (
          <div className={styles.monitoringContainer}>
            <div className={styles.monitoringHeader}>
              <h2>Sistem İzleme</h2>
              <p className={styles.monitoringSubtitle}>Sistem sağlığı ve performans metrikleri</p>
            </div>
            
            <div className={styles.monitoringGrid}>
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <span className={styles.metricDot} style={{ background: dbStats.connected ? '#22c55e' : '#ef4444' }}></span>
                  <h3>Sistem Durumu</h3>
                </div>
                <div className={styles.metricValue}>
                  {dbStats.loading ? '...' : dbStats.connected ? 'Sağlıklı' : 'Hata'}
                </div>
                <div className={styles.metricLabel}>
                  {dbStats.loading 
                    ? 'Kontrol ediliyor...' 
                    : dbStats.connected 
                      ? 'Tüm servisler çalışıyor' 
                      : dbStats.error || 'Bağlantı hatası'}
                </div>
                <div className={styles.metricBar}>
                  <div 
                    className={styles.metricBarFill} 
                    style={{ 
                      width: dbStats.connected ? '100%' : '0%', 
                      background: dbStats.connected ? '#22c55e' : '#ef4444' 
                    }}
                  ></div>
                </div>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <span className={styles.metricDot} style={{ background: '#3b82f6' }}></span>
                  <h3>Veritabanı</h3>
                </div>
                <div className={styles.metricValue}>
                  {dbStats.loading ? '...' : dbStats.totalTables}
                </div>
                <div className={styles.metricLabel}>
                  {dbStats.loading ? 'Yükleniyor...' : `${dbStats.totalSchemas} şemada toplam tablo`}
                </div>
                <div className={styles.metricBar}>
                  <div 
                    className={styles.metricBarFill} 
                    style={{ 
                      width: dbStats.totalTables > 0 ? '100%' : '0%', 
                      background: '#3b82f6' 
                    }}
                  ></div>
                </div>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <span className={styles.metricDot} style={{ background: '#3b82f6' }}></span>
                  <h3>Toplam Kullanıcı</h3>
                </div>
                <div className={styles.metricValue}>
                  {usersLoading ? '...' : totalUsers !== null ? totalUsers : '--'}
                </div>
                <div className={styles.metricLabel}>
                  {usersLoading ? 'Yükleniyor...' : 'Kayıtlı kullanıcı sayısı'}
                </div>
                <div className={styles.metricBar}>
                  <div 
                    className={styles.metricBarFill} 
                    style={{ 
                      width: totalUsers ? `${Math.min((totalUsers / 100) * 100, 100)}%` : '0%', 
                      background: '#3b82f6' 
                    }}
                  ></div>
                </div>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <span className={styles.metricDot} style={{ background: '#10b981' }}></span>
                  <h3>Aktif Kullanıcılar</h3>
                </div>
                <div className={styles.metricValue}>
                  {totalUsers !== null ? Math.floor(totalUsers * 0.1) : '--'}
                </div>
                <div className={styles.metricLabel}>Tahmini aktif (son 1 saat)</div>
                <div className={styles.metricTrend}>Aktif</div>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <span className={styles.metricDot} style={{ background: '#8b5cf6' }}></span>
                  <h3>Veritabanı Şemaları</h3>
                </div>
                <div className={styles.metricValue}>
                  {dbStats.loading ? '...' : dbStats.totalSchemas}
                </div>
                <div className={styles.metricLabel}>
                  {dbStats.loading ? 'Yükleniyor...' : 'Toplam şema sayısı'}
                </div>
                <div className={styles.metricTrend}>
                  {dbStats.connected ? 'Bağlı' : 'Bağlantı yok'}
                </div>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <span className={styles.metricDot} style={{ background: '#f59e0b' }}></span>
                  <h3>Son Güncelleme</h3>
                </div>
                <div className={styles.metricValue}>
                  {new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div className={styles.metricLabel}>Gerçek zamanlı veri</div>
                <div className={styles.metricTrend}>Canlı</div>
              </div>
            </div>
            
            <div className={styles.logSection}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0 }}>Son Sistem Logları</h3>
                {logsTotal > 0 && (
                  <span style={{ 
                    color: '#64748b', 
                    fontSize: '13px',
                    fontWeight: '500'
                  }}>
                    Toplam {logsTotal} log • Sayfa {logsPage} / {Math.ceil(logsTotal / logsPageSize)}
                  </span>
                )}
              </div>
              
              {logsLoading ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                  Log verisi yükleniyor...
                </div>
              ) : systemLogs.length > 0 ? (
                <>
                  <div className={styles.logContainer}>
                    {systemLogs.map((log, index) => {
                      const levelColor = 
                        log.level === 'INFO' ? '#3b82f6' :
                        log.level === 'SUCCESS' ? '#22c55e' :
                        log.level === 'WARNING' ? '#f59e0b' :
                        log.level === 'ERROR' ? '#ef4444' : '#64748b';
                      
                      return (
                        <div key={log.id || index} className={styles.logEntry} style={{
                          padding: '12px',
                          borderBottom: '1px solid #e2e8f0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          fontSize: '13px'
                        }}>
                          <span style={{ 
                            color: '#64748b', 
                            minWidth: '140px',
                            fontFamily: 'monospace',
                            fontSize: '12px'
                          }}>
                            {log.date} {log.time}
                          </span>
                          <span 
                            className={styles.logLevel}
                            style={{ 
                              color: levelColor,
                              fontWeight: '600',
                              minWidth: '80px',
                              fontSize: '12px'
                            }}
                          >
                            {log.level}
                          </span>
                          {log.category && (
                            <span style={{
                              color: '#94a3b8',
                              fontSize: '11px',
                              background: '#f1f5f9',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              minWidth: '80px',
                              textAlign: 'center'
                            }}>
                              {log.category}
                            </span>
                          )}
                          <span className={styles.logMessage} style={{ flex: 1 }}>
                            {log.message}
                          </span>
                          {log.userEmail && (
                            <span style={{
                              color: '#64748b',
                              fontSize: '11px',
                              minWidth: '180px',
                              textAlign: 'right'
                            }}>
                              {log.userEmail}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Pagination Controls */}
                  {logsTotal > logsPageSize && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '12px',
                      marginTop: '20px',
                      paddingTop: '20px',
                      borderTop: '1px solid #e2e8f0'
                    }}>
                      <button
                        onClick={() => setLogsPage(prev => Math.max(1, prev - 1))}
                        disabled={logsPage === 1}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: logsPage === 1 ? '#e2e8f0' : '#3b82f6',
                          color: logsPage === 1 ? '#94a3b8' : '#ffffff',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: logsPage === 1 ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (logsPage !== 1) {
                            e.target.style.backgroundColor = '#2563eb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (logsPage !== 1) {
                            e.target.style.backgroundColor = '#3b82f6';
                          }
                        }}
                      >
                        ← Önceki
                      </button>
                      
                      <span style={{
                        color: '#64748b',
                        fontSize: '14px',
                        fontWeight: '500',
                        minWidth: '120px',
                        textAlign: 'center'
                      }}>
                        Sayfa {logsPage} / {Math.ceil(logsTotal / logsPageSize)}
                      </span>
                      
                      <button
                        onClick={() => setLogsPage(prev => Math.min(Math.ceil(logsTotal / logsPageSize), prev + 1))}
                        disabled={logsPage >= Math.ceil(logsTotal / logsPageSize)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: logsPage >= Math.ceil(logsTotal / logsPageSize) ? '#e2e8f0' : '#3b82f6',
                          color: logsPage >= Math.ceil(logsTotal / logsPageSize) ? '#94a3b8' : '#ffffff',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: logsPage >= Math.ceil(logsTotal / logsPageSize) ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (logsPage < Math.ceil(logsTotal / logsPageSize)) {
                            e.target.style.backgroundColor = '#2563eb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (logsPage < Math.ceil(logsTotal / logsPageSize)) {
                            e.target.style.backgroundColor = '#3b82f6';
                          }
                        }}
                      >
                        Sonraki →
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                  Henüz log kaydı yok
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'aski-live' && (
          <ASKILiveData />
        )}

        {activeTab === 'park-verify' && (
          <ParkVerification />
        )}

        {activeTab === 'aski-analysis' && (
          <AskiAnalysis />
        )}

        {activeTab === 'aski-report' && (
          <ASKIMonthlyReport />
        )}

        {activeTab === 'data-inventory' && (
          <DataInventoryReport />
        )}

        {activeTab === 'gis-analysis' && (
          <GISDataAnalysis />
        )}

        {activeTab === 'rapor' && (
          <AdminRapor />
        )}

        {activeTab === 'ai-forecast' && (
          <ProphetForecast />
        )}

        {activeTab === 'anomaly' && (
          <AnomalyDetection />
        )}

        {activeTab === 'iot' && (
          <IoTDashboard />
        )}
      </div>

      <div className={styles.dashboardFooter}>
        <span>Pronuve Su Yönetimi</span>
        <span className={styles.footerSep}>|</span>
        <span>&copy; 2026 Pronuve Yazılım</span>
      </div>
    </div>
  );
};

export default AdminDashboard;

